import React, { useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link as MuiLink,
  CircularProgress,
  Divider,
  Box,
  Button,
  Breadcrumbs,
  Alert,
  Chip,
  Tooltip,
  Paper,
  Card,
  CardContent,
  Collapse,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faDollarSign,
  faClipboardList,
  faGavel,
  faLink,
  faToggleOn,
  faFileAlt,
  faListCheck,
  faOption,
  faFileContract,
  faMicrochipAi,
  faCalendarAlt,
  faArrowLeft,
  faLaptopCode,
} from "@fortawesome/pro-solid-svg-icons";
import HomeIcon from "@mui/icons-material/Home";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DescriptionIcon from "@mui/icons-material/Description";
import parse from "html-react-parser";
import moment from "moment";
import SetDocumentMeta from "../common/SetDocumentMeta";

const apiUrlKth = `${window._env_.REACT_APP_PATANALAI_API_URL}`;

// Styled components
const StyledAlert = styled(Alert)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(3),
}));

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  // borderLeft: `4px solid ${theme.palette.primary.main}`,
  borderLeft: `4px solid #4ABDAC`,
}));

const StyledChip = styled(Chip)(({ theme, superseded }) => ({
  marginLeft: theme.spacing(1),
  backgroundColor: superseded
    ? theme.palette.warning.main
    : theme.palette.primary.main,
  color: "#fff",
  "& .MuiChip-icon": {
    color: "#fff",
  },
}));

const AiSolutionDetail = ({ baseConfig }) => {
  SetDocumentMeta({
    title: "AI Solution Details & Legal Terms | Kama Thuo, PLLC",
    description: "",
    keywords: "",
  });
  const { tertColor, tertHoverColor, priColor } = baseConfig.siteBranding;
  const { slug } = useParams();
  const [solution, setSolution] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedSupersedeInfo, setExpandedSupersedeInfo] = useState({});

  const toggleSupersededInfo = (id) => {
    setExpandedSupersedeInfo((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  useEffect(() => {
    const fetchSolutionDetail = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${apiUrlKth}/kth-ai/solutions?solution=${slug}`
        );
        setSolution(response.data);
      } catch (err) {
        setError("Failed to fetch AI solution detail.");
        console.error("Error fetching AI solution detail:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSolutionDetail();
  }, [slug]);

  const formatDate = (date) => moment(date).format("MMM D, YYYY");

  const formatParameterCount = (count) => {
    if (!count && count !== 0) return "N/A";
    if (count >= 1_000_000_000) {
      return `${(count / 1_000_000_000).toFixed(0)} billion`;
    } else if (count >= 1_000_000) {
      return `${(count / 1_000_000).toFixed(0)} million`;
    } else if (count >= 1_000) {
      return `${(count / 1_000).toFixed(0)} thousand`;
    }
    return count.toString();
  };

  if (isLoading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <StyledAlert severity="error" variant="filled">
          {error}
        </StyledAlert>
        <Button
          startIcon={<FontAwesomeIcon icon={faArrowLeft} />}
          color="primary"
          variant="contained"
          component={RouterLink}
          to="/patent-ai-resources"
          sx={{
            mt: 1,
            mb: 2,
            backgroundColor: tertColor,
            color: "#fff",
            "&:hover": {
              backgroundColor: tertHoverColor,
              color: "#fff",
            },
          }}
        >
          Return to AI Solutions Search
        </Button>
      </Container>
    );
  }

  if (!solution)
    return (
      <Container className="link-no-decoration">
        <Typography>No solution data available.</Typography>
      </Container>
    );

  return (
    <Container className="link-no-decoration">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          {/* Breadcrumbs Navigation */}
          <Breadcrumbs
            separator={<ChevronRightIcon fontSize="small" />}
            sx={{ mt: 3, mb: 3 }}
          >
            <MuiLink
              component={RouterLink}
              to="/ai"
              sx={{ display: "flex", alignItems: "center" }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
              AI Solutions Home
            </MuiLink>
            <MuiLink
              component={RouterLink}
              to="/patent-ai-resources"
              sx={{ display: "flex", alignItems: "center" }}
            >
              <LibraryBooksIcon sx={{ mr: 0.5 }} fontSize="small" />
              AI Solutions Search
            </MuiLink>
            <Typography
              color="text.primary"
              sx={{ display: "flex", alignItems: "center" }}
            >
              <DescriptionIcon sx={{ mr: 0.5 }} fontSize="small" />
              {solution.name}
            </Typography>
          </Breadcrumbs>
        </Grid>

        {/* Solution Name */}
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Typography variant="h4" gutterBottom>
            {solution.name}
          </Typography>
        </Grid>

        {/* Solution Overview Section */}
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                <FontAwesomeIcon
                  icon={faListCheck}
                  style={{ marginRight: 12 }}
                />
                Overview
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {solution.provider && (
                  <Grid item xs={12}>
                    <Typography>
                      <FontAwesomeIcon
                        icon={faBuilding}
                        style={{ marginRight: 8 }}
                      />
                      Provider: {solution.provider}
                    </Typography>
                  </Grid>
                )}
                {solution.solution_type && (
                  <Grid item xs={12}>
                    <Typography>
                      <FontAwesomeIcon
                        icon={faMicrochipAi}
                        style={{ marginRight: 8 }}
                      />
                      <MuiLink
                        component={RouterLink}
                        to={`/patent-ai-resources#${solution.solution_type.slug}`}
                      >
                        {solution.solution_type.name}
                      </MuiLink>
                    </Typography>
                  </Grid>
                )}
                {solution.description && (
                  <Grid item xs={12}>
                    <Typography>{parse(solution.description)}</Typography>
                  </Grid>
                )}
                {solution.solution_link && (
                  <Grid item xs={12}>
                    <MuiLink
                      href={solution.solution_link}
                      target="_blank"
                      rel="noopener"
                    >
                      Visit {solution.name}
                    </MuiLink>
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Partition between sections */}
        <Divider sx={{ mt: 3, mb: 3 }} />

        {/* Solution Models Section */}
        {solution.ai_models && solution.ai_models.length > 0 && (
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">
                  <FontAwesomeIcon
                    icon={faLaptopCode}
                    style={{ marginRight: 12 }}
                  />
                  Models
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {solution.ai_models.map((model, index) => (
                    <Grid item xs={12} key={index}>
                      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                        <Typography variant="h6">
                          <MuiLink
                            component={RouterLink}
                            to={`/ai-models/${model.slug}`}
                          >
                            {model.title}
                          </MuiLink>
                        </Typography>

                        {model.parameter_count && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>Parameters:</strong>{" "}
                            {formatParameterCount(model.parameter_count)}
                          </Typography>
                        )}

                        {model.brief_description && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {parse(model.brief_description)}
                          </Typography>
                        )}

                        {/* New fields */}
                        {model.description && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {model.description}
                          </Typography>
                        )}

                        {model.released && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            <FontAwesomeIcon
                              icon={faCalendarAlt}
                              style={{ marginRight: 8 }}
                            />
                            <strong>First Used in Solution:</strong>{" "}
                            {formatDate(model.released)}
                            <Tooltip title="This is the date the model was used in the AI Tool Solution and not the date the model was released generally.">
                              <InfoIcon
                                fontSize="small"
                                sx={{
                                  ml: 1,
                                  verticalAlign: "middle",
                                  color: "info.main",
                                }}
                              />
                            </Tooltip>
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}

        {solution.ai_models && solution.ai_models.length > 0 && (
          <Divider sx={{ mt: 3, mb: 3 }} />
        )}

        {/* Solution Variants Section - UPDATED */}
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                <FontAwesomeIcon icon={faOption} style={{ marginRight: 12 }} />
                Variants
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {solution.solution_variants.map((variant, index) => (
                  <Grid item xs={12} key={index}>
                    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                      {variant.name && (
                        <Typography variant="h6">{variant.name}</Typography>
                      )}

                      {/* Subscription information */}
                      {variant.ai_subscription && (
                        <Typography sx={{ ml: 2, mb: 1 }}>
                          <FontAwesomeIcon
                            icon={faDollarSign}
                            style={{ marginRight: 8 }}
                          />
                          <Typography
                            component="span"
                            sx={{ fontWeight: "medium" }}
                          >
                            {variant.ai_subscription}
                          </Typography>
                        </Typography>
                      )}

                      {/* Released date information */}
                      {variant.released && (
                        <Typography sx={{ ml: 2, mb: 1 }}>
                          <FontAwesomeIcon
                            icon={faCalendarAlt}
                            style={{ marginRight: 8 }}
                          />
                          <Typography
                            component="span"
                            sx={{ fontWeight: "medium" }}
                          >
                            Released: {formatDate(variant.released)}
                          </Typography>
                        </Typography>
                      )}

                      {variant.description && (
                        <Typography sx={{ ml: 2, mt: 2 }}>
                          {variant.description}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Divider sx={{ mt: 3, mb: 3 }} />

        {/* Solution Compliance Section */}
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                <FontAwesomeIcon icon={faGavel} style={{ marginRight: 12 }} />
                Legal Compliance
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {solution.compliance_for_solution.map((compliance, index) => (
                  <Grid item xs={12} key={index}>
                    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                      {compliance.compliance && (
                        <Typography
                          component={RouterLink}
                          to={`/legal-compliance-for-ai/${compliance.compliance}`}
                          variant="h6"
                        >
                          {compliance.compliance_name}
                        </Typography>
                      )}
                      {compliance.compliance_notes && (
                        <Typography sx={{ mt: 1, ml: 2 }}>
                          <strong>Notes:</strong> {compliance.compliance_notes}
                        </Typography>
                      )}
                      {compliance.link_to_providers_compliance_page && (
                        <Typography sx={{ mt: 1, ml: 2 }}>
                          <MuiLink
                            href={compliance.link_to_providers_compliance_page}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FontAwesomeIcon
                              icon={faLink}
                              style={{ marginRight: 8 }}
                            />
                            Provider's Compliance Documentation
                          </MuiLink>
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Divider sx={{ mt: 3, mb: 3 }} />

        {/* Solution Legal Terms Section - IMPROVED */}
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                <FontAwesomeIcon
                  icon={faFileContract}
                  style={{ marginRight: 12 }}
                />
                Legal Terms
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {solution.agreement_terms_for_solution.map(
                  (agreementTerm, index) => (
                    <Grid item xs={12} key={index}>
                      <StyledCard>
                        <CardContent>
                          {agreementTerm.agreement_term_short && (
                            <Typography variant="h6" gutterBottom>
                              {agreementTerm.agreement_term_short}
                              {agreementTerm.agreement &&
                                agreementTerm.agreement.superseded && (
                                  <Tooltip title="This agreement has been superseded">
                                    <StyledChip
                                      size="small"
                                      label="Superseded"
                                      icon={<WarningIcon />}
                                      superseded={true}
                                      onClick={() =>
                                        toggleSupersededInfo(agreementTerm.id)
                                      }
                                    />
                                  </Tooltip>
                                )}
                            </Typography>
                          )}

                          {/* Superseded info section */}
                          {agreementTerm.agreement &&
                            agreementTerm.agreement.superseded && (
                              <Collapse
                                in={expandedSupersedeInfo[agreementTerm.id]}
                              >
                                <Box
                                  sx={{
                                    mb: 2,
                                    p: 1,
                                    bgcolor: "warning.light",
                                    color: "white",
                                    borderRadius: 1,
                                  }}
                                >
                                  <Typography variant="body2">
                                    <strong>Superseded Date:</strong>{" "}
                                    {agreementTerm.agreement.superseded_date &&
                                      formatDate(
                                        agreementTerm.agreement.superseded_date
                                      )}
                                  </Typography>
                                  {agreementTerm.agreement
                                    .superseded_changelog && (
                                    <Typography variant="body2">
                                      {/* <strong>Changes:</strong>{" "} */}
                                      {parse(
                                        agreementTerm.agreement
                                          .superseded_changelog
                                      )}
                                    </Typography>
                                  )}
                                </Box>
                              </Collapse>
                            )}

                          {agreementTerm.agreement_term && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                mb: 1,
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: "medium", color: tertColor }}
                              >
                                <span>
                                  {agreementTerm.agreement_term}:&nbsp;
                                </span>
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  ml: 1,
                                  fontWeight: "bold",
                                  color: priColor,
                                }}
                              >
                                {agreementTerm.agreement_term_status}
                              </Typography>
                            </Box>
                          )}

                          {agreementTerm.agreement_term_status_qualify && (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              sx={{ mb: 2, fontStyle: "italic" }}
                            >
                              ({agreementTerm.agreement_term_status_qualify})
                            </Typography>
                          )}

                          {agreementTerm.agreement && (
                            <Box
                              sx={{
                                p: 1.5,
                                bgcolor: "background.paper",
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                mb: 2,
                              }}
                            >
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>
                                  {agreementTerm.agreement.agreement_type
                                    ? `${agreementTerm.agreement.agreement_type} applies to:`
                                    : "Legal term applies to:"}
                                </strong>
                              </Typography>

                              {agreementTerm.agreement.applies_to_variant &&
                                agreementTerm.agreement.applies_to_variant
                                  .length > 0 && (
                                  <Box sx={{ mb: 1 }}>
                                    <Typography
                                      variant="body2"
                                      component="span"
                                    >
                                      Variants:
                                    </Typography>
                                    {agreementTerm.agreement.applies_to_variant.map(
                                      (variant, i) => (
                                        <Chip
                                          key={i}
                                          label={variant}
                                          size="small"
                                          sx={{ ml: 1, mb: 0.5 }}
                                        />
                                      )
                                    )}
                                  </Box>
                                )}

                              {agreementTerm.agreement.applies_to_party &&
                                agreementTerm.agreement.applies_to_party
                                  .length > 0 && (
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      component="span"
                                    >
                                      Users:
                                    </Typography>
                                    {agreementTerm.agreement.applies_to_party.map(
                                      (party, i) => (
                                        <Chip
                                          key={i}
                                          label={party}
                                          size="small"
                                          sx={{ ml: 1, mb: 0.5 }}
                                        />
                                      )
                                    )}
                                  </Box>
                                )}
                            </Box>
                          )}

                          {agreementTerm.agreement &&
                            agreementTerm.agreement.title && (
                              <Typography
                                variant="body2"
                                sx={{
                                  mb: 1,
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={faFileAlt}
                                  style={{ marginRight: 8 }}
                                />
                                <span>
                                  <i>See</i>&nbsp;
                                </span>
                                {agreementTerm.agreement.agreement_link ? (
                                  <MuiLink
                                    href={
                                      agreementTerm.agreement.agreement_link
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {agreementTerm.agreement.title}
                                  </MuiLink>
                                ) : (
                                  agreementTerm.agreement.title
                                )}
                                {agreementTerm.agreement_cite && (
                                  <span>
                                    &nbsp;at {agreementTerm.agreement_cite}
                                  </span>
                                )}
                                {agreementTerm.agreement.version_date && (
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ ml: 1 }}
                                  >
                                    (version date:{" "}
                                    {formatDate(
                                      agreementTerm.agreement.version_date
                                    )}
                                    )
                                  </Typography>
                                )}
                              </Typography>
                            )}

                          {agreementTerm.agreement &&
                            agreementTerm.agreement.agreement_summary && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2">
                                  Summary:
                                </Typography>
                                <Typography variant="body2" sx={{ ml: 2 }}>
                                  {parse(
                                    agreementTerm.agreement.agreement_summary
                                  )}
                                </Typography>
                              </Box>
                            )}

                          {agreementTerm.agreement &&
                            agreementTerm.agreement.agreement_discussion && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2">
                                  Discussion:
                                </Typography>
                                <Typography variant="body2" sx={{ ml: 2 }}>
                                  {parse(
                                    agreementTerm.agreement.agreement_discussion
                                  )}
                                </Typography>
                              </Box>
                            )}

                          {agreementTerm.agreement_excerpt && (
                            <Box
                              sx={{
                                mt: 2,
                                p: 1.5,
                                bgcolor: "grey.50",
                                borderLeft: "4px solid",
                                borderColor: "grey.300",
                                borderRadius: "0 4px 4px 0",
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Legal Agreement Excerpt:
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontStyle: "italic" }}
                              >
                                {parse(agreementTerm.agreement_excerpt)}
                              </Typography>
                            </Box>
                          )}

                          {agreementTerm.discussion && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2">
                                Legal Agreement Analysis:
                              </Typography>
                              <Typography variant="body2" sx={{ ml: 2 }}>
                                {parse(agreementTerm.discussion)}
                              </Typography>
                            </Box>
                          )}
                        </CardContent>
                      </StyledCard>
                    </Grid>
                  )
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid item xs={12}>
          <Button
            startIcon={<FirstPageIcon />}
            color="primary"
            component={RouterLink}
            to="/patent-ai-resources"
            sx={{ mt: 2, mb: 1 }}
          >
            Return to AI Solution Search
          </Button>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AiSolutionDetail;
